import React, { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Box, CssBaseline, ThemeProvider, Toolbar, useMediaQuery } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import adminTheme from "./theme";
import AdminSidebar from "./layout/AdminSidebar";
import AdminHeader from "./layout/AdminHeader";
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH, HEADER_HEIGHT } from "./layout/layoutConstants";

const COLLAPSE_STORAGE_KEY = "bigmarket_admin_sidebar_collapsed";

function readStoredCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery(adminTheme.breakpoints.up("md"));

  function handleMenuClick() {
    if (isMdUp) {
      setCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? "1" : "0");
        } catch {
          // ignore storage failures (e.g. private browsing)
        }
        return next;
      });
    } else {
      setMobileOpen((prev) => !prev);
    }
  }

  function handleLogout() {
    logout();
    navigate("/admin/login");
  }

  const desktopWidth = collapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline />
      {/* This console has no Arabic/RTL translations - force LTR so it doesn't inherit
          dir="rtl" from <html> when the site-wide (client-facing) language is Arabic,
          which would mirror the flex layout and break the fixed sidebar/content positioning. */}
      <Box dir="ltr" sx={{ display: "flex", minHeight: "100vh" }}>
        <AdminHeader collapsed={collapsed} onMenuClick={handleMenuClick} user={user} onLogout={handleLogout} />

        <AdminSidebar
          mobileOpen={mobileOpen}
          onCloseMobile={() => setMobileOpen(false)}
          collapsed={collapsed}
          onToggleCollapse={handleMenuClick}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - ${desktopWidth}px)` },
            bgcolor: "background.default",
            minHeight: "100vh",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important` }} />
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
